<div class="app-page">
  <!-- Header -->
  <div class="app-page-header">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="section-title">{{ isEditMode() ? 'Editar Cliente' : 'Nuevo Cliente' }}</h1>
        <p class="section-subtitle">
          {{ isEditMode() ? 'Modifica la información del cliente' : 'Completa los datos para crear un nuevo cliente' }}
        </p>
      </div>
      <div class="flex gap-2">
        <button 
          pButton 
          type="button"
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-outlined"
          (click)="onCancel()"
          [disabled]="loading()">
        </button>
        <button 
          pButton 
          type="button"
          [label]="isEditMode() ? 'Guardar Cambios' : 'Crear Cliente'"
          [icon]="isEditMode() ? 'pi pi-check' : 'pi pi-plus'"
          class="p-button"
          (click)="onSubmit()"
          [disabled]="loading() || clienteForm.invalid"
          [loading]="loading()">
        </button>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="app-page-content edit-layout">
    <form [formGroup]="clienteForm" (ngSubmit)="onSubmit()" id="clienteForm" class="edit-form">
      <div class="app-card">
        <!-- Form Title -->
        <h2 class="text-xl font-semibold mb-2" style="color: hsl(var(--text));">
          {{ isEditMode() ? 'Datos del Cliente' : 'Datos Generales' }}
        </h2>
        
        <!-- Leyenda de Iconos -->
        <div class="mb-4 p-3 rounded-lg" style="background-color: hsl(var(--surface-2));">
          <div class="flex flex-wrap items-center gap-4 text-xs" style="color: hsl(var(--muted));">
            <span class="font-medium">Referencia de iconos:</span>
            <div class="flex items-center gap-1.5">
              <i class="pi pi-megaphone text-base" style="color: hsl(var(--primary));"></i>
              <span>Anunciante</span>
            </div>
            <div class="flex items-center gap-1.5">
              <i class="pi pi-building text-base" style="color: hsl(var(--success));"></i>
              <span>Agencia</span>
            </div>
            <div class="flex items-center gap-1.5">
              <i class="pi pi-briefcase text-base" style="color: hsl(var(--warning));"></i>
              <span>Productor</span>
            </div>
          </div>
        </div>
        
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 mt-6">
          <!-- Nombre -->
          <div class="space-y-2">
            <label for="nombre" class="text-sm font-medium" style="color: hsl(var(--text));">
              Nombre *
            </label>
            <input 
              pInputText 
              id="nombre" 
              formControlName="nombre"
              type="text"
              placeholder="Juan"
              class="w-full"
              [class.border-red-500]="clienteForm.get('nombre')?.invalid && clienteForm.get('nombre')?.touched"
            />
            @if (clienteForm.get('nombre')?.invalid && clienteForm.get('nombre')?.touched) {
              <small class="text-sm block" style="color: hsl(var(--danger));">
                Nombre es requerido
              </small>
            }
          </div>

          <!-- Apellido -->
          <div class="space-y-2">
            <label for="apellido" class="text-sm font-medium" style="color: hsl(var(--text));">
              Apellido *
            </label>
            <input 
              pInputText 
              id="apellido" 
              formControlName="apellido"
              type="text"
              placeholder="Pérez"
              class="w-full"
              [class.border-red-500]="clienteForm.get('apellido')?.invalid && clienteForm.get('apellido')?.touched"
            />
            @if (clienteForm.get('apellido')?.invalid && clienteForm.get('apellido')?.touched) {
              <small class="text-sm block" style="color: hsl(var(--danger));">
                Apellido es requerido
              </small>
            }
          </div>

          <!-- Razón Social -->
          <div class="space-y-2">
            <label for="razonSocial" class="text-sm font-medium" style="color: hsl(var(--text));">
              Razón Social *
            </label>
            <input 
              pInputText 
              id="razonSocial" 
              formControlName="razonSocial"
              type="text"
              placeholder="Mi Empresa S.A."
              class="w-full"
              [class.border-red-500]="clienteForm.get('razonSocial')?.invalid && clienteForm.get('razonSocial')?.touched"
            />
            @if (clienteForm.get('razonSocial')?.invalid && clienteForm.get('razonSocial')?.touched) {
              <small class="text-sm block" style="color: hsl(var(--danger));">
                Razón Social es requerida
              </small>
            }
          </div>

          <!-- CUIT -->
          <div class="space-y-2">
            <label for="cuit" class="text-sm font-medium" style="color: hsl(var(--text));">
              CUIT *
            </label>
            <input 
              pInputText 
              id="cuit" 
              formControlName="cuit"
              type="text"
              placeholder="20-12345678-9"
              class="w-full"
              [class.border-red-500]="clienteForm.get('cuit')?.invalid && clienteForm.get('cuit')?.touched"
            />
            @if (clienteForm.get('cuit')?.hasError('required') && clienteForm.get('cuit')?.touched) {
              <small class="text-sm block" style="color: hsl(var(--danger));">
                CUIT es requerido
              </small>
            }
            @if (clienteForm.get('cuit')?.hasError('server')) {
              <small class="text-sm block" style="color: hsl(var(--danger));">
                {{ clienteForm.get('cuit')?.getError('server') }}
              </small>
            }
          </div>

          <!-- Nombre de Fantasía -->
          <div class="space-y-2">
            <label for="nombreFantasia" class="text-sm font-medium" style="color: hsl(var(--text));">
              Nombre de Fantasía
            </label>
            <input 
              pInputText 
              id="nombreFantasia" 
              formControlName="nombreFantasia"
              type="text"
              placeholder="Nombre conocido"
              class="w-full"
            />
          </div>

          <!-- Tipo de Cliente -->
          <div class="space-y-2">
            <label for="tipoCliente" class="text-sm font-medium" style="color: hsl(var(--text));">
              Tipo de Cliente *
            </label>
            <p-select
              id="tipoCliente"
              formControlName="tipoCliente"
              [options]="tipoClienteOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Seleccionar tipo..."
              class="w-full"
              [class.border-red-500]="clienteForm.get('tipoCliente')?.invalid && clienteForm.get('tipoCliente')?.touched">
              <ng-template let-option pTemplate="item">
                <div class="flex items-center gap-2">
                  <i 
                    [class]="getTipoClienteInfo(option.value).icon"
                    class="text-base"
                    [style.color]="getTipoClienteInfo(option.value).color">
                  </i>
                  <span>{{ option.label }}</span>
                </div>
              </ng-template>
              <ng-template let-option pTemplate="selectedItem">
                <div class="flex items-center gap-2">
                  <i 
                    [class]="getTipoClienteInfo(option.value).icon"
                    class="text-base"
                    [style.color]="getTipoClienteInfo(option.value).color">
                  </i>
                  <span>{{ option.label }}</span>
                </div>
              </ng-template>
            </p-select>
            @if (clienteForm.get('tipoCliente')?.invalid && clienteForm.get('tipoCliente')?.touched) {
              <small class="text-sm block" style="color: hsl(var(--danger));">
                Tipo de Cliente es requerido
              </small>
            }
          </div>

          <!-- Agencia/Productor (solo para Anunciante) -->
          @if (showAgenciaProductor()) {
            <div class="space-y-2">
              <label for="agenciaProductorCliente" class="text-sm font-medium" style="color: hsl(var(--text));">
                Agencia/Productor
              </label>
              <div class="relative w-full" [class.agencia-productor-with-icon]="clienteForm.get('agenciaProductorCliente')?.value">
                @if (clienteForm.get('agenciaProductorCliente')?.value) {
                  <i 
                    [class]="getAgenciaProductorIcon(clienteForm.get('agenciaProductorCliente')?.value).icon"
                    class="absolute left-3 top-1/2 -translate-y-1/2 text-base z-10 pointer-events-none"
                    [style.color]="getAgenciaProductorIcon(clienteForm.get('agenciaProductorCliente')?.value).tipo === 'Agencia' ? 'hsl(var(--success))' : 'hsl(var(--warning))'">
                  </i>
                }
                <p-autoComplete 
                  id="agenciaProductorCliente"
                  formControlName="agenciaProductorCliente"
                  [suggestions]="agenciasProductoresFiltradas()"
                  (completeMethod)="filtrarAgenciasProductores($event)"
                  optionLabel="razonSocial"
                  placeholder="Seleccionar agencia/productor..."
                  [showEmptyMessage]="true"
                  emptyMessage="No hay agencias/productores disponibles"
                  class="w-full"
                  [dropdown]="true"
                  [forceSelection]="true"
                  [showClear]="true">
                  <ng-template let-cliente pTemplate="item">
                    <div class="flex items-center gap-2">
                      <i 
                        [class]="getAgenciaProductorIcon(cliente).icon"
                        class="text-base"
                        [style.color]="getAgenciaProductorIcon(cliente).tipo === 'Agencia' ? 'hsl(var(--success))' : 'hsl(var(--warning))'">
                      </i>
                      <span>{{ cliente.razonSocial }}</span>
                    </div>
                  </ng-template>
                </p-autoComplete>
              </div>
            </div>
          }

          <!-- Email -->
          <div class="space-y-2">
            <label for="email" class="text-sm font-medium" style="color: hsl(var(--text));">
              Email *
            </label>
            <input 
              pInputText 
              id="email" 
              formControlName="email"
              type="email"
              placeholder="cliente@ejemplo.com"
              class="w-full"
              [class.border-red-500]="clienteForm.get('email')?.invalid && clienteForm.get('email')?.touched"
            />
            @if (clienteForm.get('email')?.invalid && clienteForm.get('email')?.touched) {
              <small class="text-sm block" style="color: hsl(var(--danger));">
                Email es requerido y debe ser válido
              </small>
            }
          </div>

          <!-- Teléfono -->
          <div class="space-y-2">
            <label for="telefono" class="text-sm font-medium" style="color: hsl(var(--text));">
              Teléfono
            </label>
            <input 
              pInputText 
              id="telefono" 
              formControlName="telefono"
              type="tel"
              placeholder="+54 11 1234-5678"
              class="w-full"
            />
          </div>

          <!-- Domicilio -->
          <div class="space-y-2">
            <label for="domicilio" class="text-sm font-medium" style="color: hsl(var(--text));">
              Domicilio
            </label>
            <input 
              pInputText 
              id="domicilio" 
              formControlName="domicilio"
              type="text"
              placeholder="Calle 123, Ciudad"
              class="w-full"
            />
          </div>

          <!-- Provincia -->
          <div class="space-y-2">
            <label for="provincia" class="text-sm font-medium" style="color: hsl(var(--text));">
              Provincia
            </label>
            <p-select
              id="provincia"
              formControlName="provincia"
              [options]="provincias()"
              optionLabel="nombre"
              optionValue="id"
              placeholder="Seleccionar provincia..."
              class="w-full">
            </p-select>
          </div>

          <!-- Localidad -->
          @if (showLocalidad()) {
            <div class="space-y-2">
              <label for="localidad" class="text-sm font-medium" style="color: hsl(var(--text));">
                Localidad
              </label>
              <p-autoComplete 
                id="localidad"
                formControlName="localidad"
                [suggestions]="localidadSuggestions()"
                (completeMethod)="filterLocalidades($event)"
                optionLabel="distritoNombre"
                placeholder="Seleccionar localidad..."
                [showEmptyMessage]="true"
                emptyMessage="No hay localidades disponibles"
                class="w-full"
                [dropdown]="true"
                [forceSelection]="true">
              </p-autoComplete>
            </div>
          }

          <!-- Condición IVA -->
          <div class="space-y-2">
            <label for="condicionIva" class="text-sm font-medium" style="color: hsl(var(--text));">
              Condición IVA
            </label>
            <p-select
              id="condicionIva"
              formControlName="condicionIva"
              [options]="condicionIvaOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Seleccionar condición..."
              class="w-full">
            </p-select>
          </div>

          <!-- Observaciones -->
          <div class="space-y-2 md:col-span-2 lg:col-span-3">
            <label for="observaciones" class="text-sm font-medium" style="color: hsl(var(--text));">
              Observaciones
            </label>
            <textarea 
              pTextarea
              id="observaciones" 
              formControlName="observaciones"
              placeholder="Observaciones adicionales"
              class="w-full"
              rows="3">
            </textarea>
          </div>

          <!-- Estado -->
          <div class="space-y-2 md:col-span-2 lg:col-span-3">
            <div class="flex items-center justify-between p-4 rounded-lg" style="background-color: hsl(var(--surface-2));">
              <div>
                <label for="active" class="text-sm font-medium" style="color: hsl(var(--text));">
                  Cliente Activo
                </label>
                <p class="text-sm text-muted mt-1">
                  Indica si el cliente está activo en el sistema
                </p>
              </div>
              <p-toggleSwitch 
                id="active"
                formControlName="active">
              </p-toggleSwitch>
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- Panel de Anunciantes (solo en modo edición, desktop sticky) -->
    @if (isEditMode() && cliente()) {
      <div class="edit-sidebar">
        <app-cliente-anunciantes-panel [cliente]="cliente()"></app-cliente-anunciantes-panel>
      </div>
    }
  </div>
</div>
